import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useCurrency } from "../lib/currency";
import DashboardHeader from "../components/DashboardHeader";
import EncomendaForm from "../components/EncomendaForm";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Plus, ClipboardList, Trash2, Package, ArrowRight, Loader2 } from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";

export default function ShoppingList() {
  const { user } = useAuth();
  const { format } = useCurrency();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState({});
  const [encomendaOpen, setEncomendaOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: prods }] = await Promise.all([
        api.get("/shopping-list"),
        api.get("/products"),
      ]);
      setItems(list || []);
      setProducts(prods || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (user) load(); }, [user]);

  if (user === undefined) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">A carregar…</div>;
  if (user === null) return <Navigate to="/login" replace />;

  const remove = async (id) => {
    try { await api.delete(`/shopping-list/${id}`); toast.success("Removido"); load(); }
    catch { toast.error("Erro ao remover"); }
  };

  const openNew = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (it) => { setEditing(it); setFormOpen(true); };

  const totalEstimated = items.reduce((a, i) => a + (Number(i.estimated_price) || 0) * (i.quantity || 1), 0);

  const selectedItems = items.filter((it) => selected[it.id]);
  const convertToEncomenda = () => {
    if (selectedItems.length === 0) { toast.error("Seleciona pelo menos um item"); return; }
    setEncomendaOpen(true);
  };

  // Build initial items for EncomendaForm from selected shopping list rows
  const initialEncomendaItems = selectedItems.map((it) => {
    const matched = products.find((p) => p.id === it.product_id);
    if (matched) {
      const variant = matched.variants?.find((v) => v.name.toLowerCase() === (it.variant_hint || "").toLowerCase());
      if (variant) {
        return {
          mode: "existing",
          product_id: matched.id,
          variant_id: variant.id,
          quantity: it.quantity,
          unit_purchase_price: it.estimated_price || 0,
          suggested_sale_price: variant.sale_price || 0,
          notes: it.notes || "",
        };
      }
      return {
        mode: "new-variant",
        product_id: matched.id,
        variant_name: it.variant_hint || "Único",
        quantity: it.quantity,
        unit_purchase_price: it.estimated_price || 0,
        notes: it.notes || "",
      };
    }
    return {
      mode: "new-product",
      product_name: it.product_name,
      variant_name: it.variant_hint || "Único",
      quantity: it.quantity,
      unit_purchase_price: it.estimated_price || 0,
      notes: it.notes || "",
    };
  });

  const afterEncomendaSaved = async () => {
    // Remove selected items from shopping list
    await Promise.all(selectedItems.map((it) => api.delete(`/shopping-list/${it.id}`).catch(() => {})));
    setSelected({});
    setEncomendaOpen(false);
    toast.success("Itens convertidos em encomenda");
    navigate("/dashboard/encomendas");
  };

  return (
    <div className="min-h-screen bg-background" data-testid="shopping-list-page">
      <DashboardHeader title="Lista de compras" />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 lb-fade-in">
          <div>
            <div className="lb-overline">Próximas compras</div>
            <h1 className="font-display text-3xl sm:text-4xl mt-1">Lista de compras</h1>
            <p className="text-sm text-muted-foreground mt-1">Anota o que precisas repor. Quando o pedido chegar, converte em encomenda com 1 clique.</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={convertToEncomenda}
              disabled={selectedItems.length === 0}
              variant="outline"
              data-testid="convert-to-encomenda-btn"
            >
              <ArrowRight className="w-4 h-4 mr-1" /> Converter em encomenda ({selectedItems.length})
            </Button>
            <Button onClick={openNew} className="bg-[hsl(var(--primary))] hover:bg-[#A21F58] text-white" data-testid="new-shopping-item-btn">
              <Plus className="w-4 h-4 mr-1" /> Adicionar item
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="space-y-3">{Array.from({length:3}).map((_,i)=><div key={i} className="h-16 bg-white rounded-2xl lb-shadow-card animate-pulse" />)}</div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl lb-shadow-card" data-testid="empty-shopping">
            <ClipboardList className="w-12 h-12 mx-auto text-muted-foreground/50" />
            <h3 className="mt-3 font-display text-xl">Lista vazia</h3>
            <p className="text-sm text-muted-foreground mt-1">Adiciona o primeiro item para a próxima encomenda.</p>
          </div>
        ) : (
          <>
            <div className="rounded-2xl bg-white lb-shadow-card divide-y divide-border">
              {items.map((it) => (
                <div key={it.id} className="p-3 sm:p-4 flex items-center gap-3" data-testid={`shopping-item-${it.id}`}>
                  <Checkbox
                    checked={!!selected[it.id]}
                    onCheckedChange={(v) => setSelected((s) => ({ ...s, [it.id]: !!v }))}
                    data-testid={`shopping-check-${it.id}`}
                  />
                  <button onClick={() => openEdit(it)} className="flex-1 text-left min-w-0">
                    <div className="font-semibold truncate">
                      {it.product_name}
                      {it.variant_hint ? <span className="text-muted-foreground font-normal"> · {it.variant_hint}</span> : null}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {it.quantity} un.
                      {it.estimated_price > 0 && ` · ~${format(it.estimated_price)}/un`}
                      {it.notes && ` · ${it.notes}`}
                    </div>
                  </button>
                  <Button variant="ghost" size="icon" onClick={() => remove(it.id)} data-testid={`remove-shopping-${it.id}`}>
                    <Trash2 className="w-4 h-4 text-[hsl(var(--destructive))]" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="text-right text-sm text-muted-foreground">
              Estimado total: <span className="font-semibold text-foreground">{format(totalEstimated)}</span>
            </div>
          </>
        )}
      </main>

      <ShoppingItemForm open={formOpen} onOpenChange={setFormOpen} item={editing} products={products} onSaved={load} />

      <EncomendaForm
        open={encomendaOpen}
        onOpenChange={setEncomendaOpen}
        initialItems={initialEncomendaItems}
        onSaved={afterEncomendaSaved}
      />
    </div>
  );
}

function ShoppingItemForm({ open, onOpenChange, item, products, onSaved }) {
  const isEdit = !!item?.id;
  const [form, setForm] = useState({
    product_id: "",
    product_name: "",
    variant_hint: "",
    quantity: 1,
    estimated_price: 0,
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [pickerMode, setPickerMode] = useState("existing"); // "existing" | "manual"

  useEffect(() => {
    if (!open) return;
    if (item) {
      setForm({
        product_id: item.product_id || "",
        product_name: item.product_name || "",
        variant_hint: item.variant_hint || "",
        quantity: item.quantity || 1,
        estimated_price: item.estimated_price || 0,
        notes: item.notes || "",
      });
      setPickerMode(item.product_id ? "existing" : "manual");
    } else {
      setForm({ product_id: "", product_name: "", variant_hint: "", quantity: 1, estimated_price: 0, notes: "" });
      setPickerMode("existing");
    }
  }, [open, item]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.product_name.trim()) { toast.error("Nome do produto é obrigatório"); return; }
    setSaving(true);
    try {
      const payload = {
        product_id: pickerMode === "existing" ? form.product_id || null : null,
        product_name: form.product_name.trim(),
        variant_hint: form.variant_hint || "",
        quantity: Number(form.quantity) || 1,
        estimated_price: Number(form.estimated_price) || 0,
        notes: form.notes || "",
      };
      if (isEdit) await api.put(`/shopping-list/${item.id}`, payload);
      else await api.post("/shopping-list", payload);
      toast.success(isEdit ? "Atualizado" : "Adicionado");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="shopping-item-form">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{isEdit ? "Editar item" : "Adicionar à lista"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="flex gap-2">
            <button type="button" onClick={() => setPickerMode("existing")} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${pickerMode === "existing" ? "bg-[hsl(var(--primary))] text-white" : "bg-secondary text-muted-foreground"}`} data-testid="picker-existing">Produto existente</button>
            <button type="button" onClick={() => setPickerMode("manual")} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${pickerMode === "manual" ? "bg-[hsl(var(--primary))] text-white" : "bg-secondary text-muted-foreground"}`} data-testid="picker-manual">Novo / escrever à mão</button>
          </div>

          {pickerMode === "existing" ? (
            <div className="space-y-2">
              <Label>Produto</Label>
              <Select
                value={form.product_id}
                onValueChange={(v) => {
                  const p = products.find((x) => x.id === v);
                  setForm({ ...form, product_id: v, product_name: p?.name || "" });
                }}
              >
                <SelectTrigger data-testid="shopping-product-select"><SelectValue placeholder="Escolher produto" /></SelectTrigger>
                <SelectContent>
                  {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="space-y-2">
              <Label>Nome do produto *</Label>
              <Input value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} placeholder="Ex: Perfume Malbec" data-testid="shopping-product-name-input" />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Variante</Label>
              <Input value={form.variant_hint} onChange={(e) => setForm({ ...form, variant_hint: e.target.value })} placeholder="Ex: 100ml" data-testid="shopping-variant-input" />
            </div>
            <div className="space-y-2">
              <Label>Quantidade</Label>
              <Input type="number" min="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="shopping-qty-input" />
            </div>
            <div className="space-y-2 col-span-2">
              <Label>Preço estimado / unidade (CVE)</Label>
              <Input type="number" step="0.01" value={form.estimated_price} onChange={(e) => setForm({ ...form, estimated_price: e.target.value })} data-testid="shopping-price-input" />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Notas</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Ex: pedido da Maria, reposição urgente..." data-testid="shopping-notes-input" />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving} className="bg-[hsl(var(--primary))] hover:bg-[#A21F58]" data-testid="save-shopping-item-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
